import React, { useEffect, useRef, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import { HiMenu } from 'react-icons/hi';
import { FaTrophy } from 'react-icons/fa';
import LeaderboardGroupedTable from '../../components/LeaderboardGroupedTable';
import { CountrySelect } from '../../components/CountrySelect';
import { fetchWeeklyLeaderboard } from '../../services/weeklyLeaderboardService';
import Toast from '../../components/toaster';

// Cache so revisiting the tab can render instantly (same pattern as Users/Quiz/Chapters).
const leaderboardCache = {
  groups: null,
  previousGroups: null,
  firstLoad: true,
};

const Leaderboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Admin search controls:
  // - country (required)
  const [countryDraft, setCountryDraft] = useState('');
  const [country, setCountry] = useState('');
  // New behavior: show nothing until the admin selects a country and searches.
  const [hasSearched, setHasSearched] = useState(false);
  const [searchVersion, setSearchVersion] = useState(0);

  const [groups, setGroups] = useState(leaderboardCache.groups || []);
  const [previousGroups, setPreviousGroups] = useState(
    leaderboardCache.previousGroups || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentWeekNumber, setCurrentWeekNumber] = useState(null);
  const weekNumber = currentWeekNumber;

  const isInitialLoading =
    loading && hasSearched && (!Array.isArray(groups) || groups.length === 0);

  const handleSearch = () => {
    const nextCountry =
      typeof countryDraft === 'string' ? countryDraft.trim() : countryDraft;

    if (!nextCountry) {
      Toast.error('Please select a country');
      return;
    }

    setError(null);
    setCountry(nextCountry);
    setHasSearched(true);
    // Force a refetch even if week/country haven't changed.
    setSearchVersion((v) => v + 1);
  };

  useEffect(() => {
    if (!hasSearched) return;
    let cancelled = false;

    const load = async () => {
      const hasCache =
        leaderboardCache.groups !== null &&
        !leaderboardCache.firstLoad &&
        leaderboardCache.country === country;

      // When revisiting the tab with same inputs, show cached data (no full-screen loader).
      if (hasCache) {
        setGroups(leaderboardCache.groups || []);
        setPreviousGroups(leaderboardCache.previousGroups || []);
        setLoading(false);
        setError(null);
        return;
      }

      const startedAt = Date.now();
      const isFirstVisit = leaderboardCache.firstLoad;

      setLoading(true);
      setError(null);
      try {
        const res = await fetchWeeklyLeaderboard({ weekNumber, country });
        if (cancelled) return;

        const nextGroups = res?.groups || [];
        const nextPreviousGroups = res?.previousGroups || [];
        setGroups(nextGroups);
        setPreviousGroups(nextPreviousGroups);

        if (res?.currentWeekNumber) {
          setCurrentWeekNumber(res.currentWeekNumber || null);
        }

        leaderboardCache.groups = nextGroups;
        leaderboardCache.previousGroups = nextPreviousGroups;
        leaderboardCache.country = country;
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load leaderboard');
      } finally {
        // Ensure "first visit loader" is visible even on very fast responses.
        if (!cancelled && isFirstVisit) {
          const minMs = 350;
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, minMs - elapsed);
          if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
        }
        if (!cancelled) setLoading(false);
        leaderboardCache.firstLoad = false;
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [weekNumber, country, hasSearched, searchVersion, countryDraft]);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden w-full">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <div className="flex items-center gap-3">
                {/* <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm border border-gray-100">
                  <FaTrophy className="w-5 h-5 text-brand" />
                </div> */}
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                  Leaderboard
                </h1>
              </div>
            </div>
            {/* <p className="mt-2 text-sm text-gray-600">
              Grouped by country. Top 3 are highlighted automatically.
            </p> */}
          </div>

          {!loading && !isInitialLoading ? (
            <div className="bg-white rounded-xl shadow-sm border max-w-xl border-gray-100 p-4 sm:p-5 mb-4 md:mb-6">
              <div className="flex flex-col md:flex-row md:items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Country
                  </label>
                  <CountrySelect
                    value={countryDraft}
                    onChange={setCountryDraft}
                    id="country"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white rounded-lg shadow-sm bg-brand hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  Search
                </button>
              </div>

              {/* <div className="mt-2 text-xs text-gray-500">
                {hasSearched ? (
                  <>
                    {loading ? 'Fetching week ' : 'Showing week '}
                    <span className="font-semibold text-gray-700">{weekNumber}</span> and{' '}
                    <span className="font-semibold text-gray-700">
                      {country ? country : 'all countries'}
                    </span>
                  </>
                ) : (
                  <>
                    Default week number is{' '}
                    <span className="font-semibold text-gray-700">
                      {currentWeekNumber}
                    </span>
                  </>
                )}
              </div> */}
            </div>
          ) : null}

          {isInitialLoading || loading ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : error ? null : (
            <>
              {hasSearched ? (
                <div>
                  {/* <div className="mb-3 text-xs text-gray-500">
                    Showing results for week{' '}
                    <span className="font-semibold text-gray-700">{weekNumber}</span>{' '}
                    and{' '}
                    <span className="font-semibold text-gray-700">
                      {country ? country : 'all countries'}
                    </span>
                  </div> */}
                  <LeaderboardGroupedTable
                    groups={groups}
                    groupsPageSize={3}
                    currentWeekNumber={currentWeekNumber}
                    previousWeekGroups={previousGroups}
                  />
                </div>
              ) : (
                <div className="mt-4 text-sm text-gray-500">
                  Select a country and click Search to view the leaderboard.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;

